import { HttpErrorResponse } from '@angular/common/http';
import { IResponse, Page, PageQuery } from '@models/common.model';
import { scan } from 'rxjs';
import Toastify from 'toastify-js';

export const saveStorage = (key: string, value: unknown) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const getStorage = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? '');
  } catch (error) {
    // console.log(error);
  }
  return null;
};

const fallbackCopyTextToClipboard = (text: string) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    const msg = successful ? 'successful' : 'unsuccessful';
    console.log('Fallback: Copying text command was ' + msg);
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err);
  }

  document.body.removeChild(textArea);
};
export const copyTextToClipboard = (text: string) => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text).then(
    function () {
      console.log('Async: Copying to clipboard was successful!');
    },
    function (err) {
      console.error('Async: Could not copy text: ', err);
    }
  );
};

export const scanInfiniteScroll = <T, R extends PageQuery>() =>
  scan<[IResponse<Page<T>>, R], Page<T>>((acc, [response, query]) => {
    if (query.page === 1) {
      return response.data;
    }
    response.data.items = [...acc.items, ...response.data.items];
    return response.data;
  }, {} as Page<T>);

export const toastSuccess = (message = 'Lưu thành công') => {
  Toastify({
    text: message,
    duration: 3000,
    newWindow: true,
    close: true,
    gravity: 'top', // `top` or `bottom`
    position: 'center', // `left`, `center` or `right`
    style: {
      background: '#fff',
      width: 'fit-content',
      maxWidth: '480px',
      borderRadius: '24px',
      color: 'black',
      display: 'flex',
      justifyContent: 'space-between',
    },
  }).showToast();
};

export const toastError = (message = 'Lưu thất bại') => {
  Toastify({
    text: message,
    duration: 3000,
    newWindow: true,
    close: true,
    gravity: 'top', // `top` or `bottom`
    position: 'center', // `left`, `center` or `right`
    style: {
      background: '#fff',
      width: 'fit-content',
      maxWidth: '480px',
      borderRadius: '24px',
      color: 'red',
      display: 'flex',
      justifyContent: 'space-between',
    },
  }).showToast();
};

export const handlerApiError = (err: unknown, message = '') => {
  if (err instanceof HttpErrorResponse) {
    if (err.status === 400) {
      toastError(err.error.message);
      return;
    }
    if (err.status === 401) {
      toastError('Bạn không có quyền thực hiện thao tác này');
      return;
    }
    if (err.status === 403) {
      toastError('Bạn không có quyền thực hiện thao tác này');
      return;
    }
    if (err.status === 404) {
      toastError('Không tìm thấy dữ liệu');
      return;
    }
    if (err.status === 500) {
      toastError(message || 'Lỗi hệ thống');
      return;
    }
  }
  toastError(message);
};
export const base64ToBlob = (base64: string, type = 'application/pdf') => {
  // Convert base64 to Uint8Array
  const binaryData = atob(base64);
  const uint8Array = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    uint8Array[i] = binaryData.charCodeAt(i);
  }
  // Create Blob from Uint8Array
  return new Blob([uint8Array], { type });
};
export const saveBlobToDownload = (blob: Blob, fileName: string) => {
  if (Object.hasOwn(window.navigator, 'msSaveOrOpenBlob')) {
    // For IE browser
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
  } else {
    // For other browsers
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = fileName;

    // Create a click event and trigger the link
    const clickEvent = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: false,
    });

    link.dispatchEvent(clickEvent);
    URL.revokeObjectURL(url);
  }
};
